/**
 * Which groups auto-sequencing has taken over, for the assign-to-a-group modal.
 *
 * Its own module so the branching is testable without mounting the modal, and
 * because "the feature is not here" and "the lookup broke" are different things
 * to tell a teacher rather than one silence.
 */
import api from "@/utils/axios-utils";

export type SequencingLookup =
  | { kind: "off" }
  | { kind: "ok"; enabledIds: Set<number> }
  | { kind: "failed" };

/** The router-level gate's answer when the environment has sequencing off. */
const isFeatureDisabled = (status: number | undefined, detail: string) =>
  // 404 by design -- `require_lesson_sequencing_enabled` returns "absent"
  // rather than "forbidden". Matched with the detail as well as the status so a
  // 404 from anything else (a route renamed, a bad deploy) still reads as a
  // failure and warns, which is the safe direction to be wrong in.
  status === 404 && /not enabled/i.test(detail);

/**
 * Which of these groups auto-sequencing has taken over, if we can tell.
 *
 * Deliberately asks the server rather than reading the client flag. The
 * client copy of `enableLessonSequencing` is only as fresh as the last
 * `client_flags` fetch, so it is not evidence about the server's answer now.
 * Skipping the request when it was off meant that during a rollout, sequenced
 * groups rendered as manually assignable and the backend refused the
 * assignment with nothing on screen explaining why.
 *
 * The cost is one request per modal open in an environment that will 404 it.
 * That is the price of the answer being true rather than inferred.
 */
export const lookUpSequencing = async (): Promise<SequencingLookup> => {
  try {
    // Not the page's overview: that is the owning teacher's alone (R31), and a
    // co-teacher assigning lessons for the lead still needs this answer.
    const { data } = await api.get<{ classroom_ids?: number[] }>(
      "/lesson-sequencing/sequenced-classrooms",
    );
    return { kind: "ok", enabledIds: new Set(data?.classroom_ids ?? []) };
  } catch (error) {
    const response = (
      error as { response?: { status?: number; data?: { detail?: unknown } } }
    )?.response;
    const detail =
      typeof response?.data?.detail === "string" ? response.data.detail : "";
    // Nothing can refuse an assignment where the feature does not exist, so
    // this one response is silence rather than a warning.
    if (isFeatureDisabled(response?.status, detail)) return { kind: "off" };
    // Every other failure is the same thing to a teacher: we cannot say which
    // groups are sequenced, and the backend will still refuse an assignment to
    // one that is.
    return { kind: "failed" };
  }
};
